import React, { useState, useEffect, useMemo, useCallback } from 'react';
import '../App.css';
import {TelegramAuth} from "./TelegramAuth";

const API_URL = window.location.origin;

// ✅ Выносим модальное окно в отдельный компонент
const ResetPasswordModal = ({ isOpen, onClose, onSuccess }) => {
    const [resetEmail, setResetEmail] = useState('');
    const [resetCode, setResetCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [resetStep, setResetStep] = useState('email');
    const [resetLoading, setResetLoading] = useState(false);
    const [resetError, setResetError] = useState(null);
    const [resetSuccess, setResetSuccess] = useState(null);

    // Сбрасываем состояние при открытии/закрытии
    useEffect(() => {
        if (!isOpen) {
            // Не сбрасываем сразу, чтобы не было мерцания
            const timer = setTimeout(() => {
                setResetStep('email');
                setResetEmail('');
                setResetCode('');
                setNewPassword('');
                setConfirmNewPassword('');
                setResetError(null);
                setResetSuccess(null);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    const handleRequestResetCode = async () => {
        if (!resetEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
            setResetError('Введите корректный email');
            return;
        }

        setResetLoading(true);
        setResetError(null);

        try {
            const response = await fetch(`${API_URL}/api/auth/request-password-reset`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: resetEmail })
            });

            const data = await response.json();

            if (data.success) {
                setResetStep('code');
                setResetSuccess('Код подтверждения отправлен на ваш email');
                setTimeout(() => setResetSuccess(null), 5000);
            } else {
                setResetError(data.error || 'Ошибка при отправке кода');
            }
        } catch (error) {
            console.error('Ошибка:', error);
            setResetError('Ошибка соединения с сервером');
        } finally {
            setResetLoading(false);
        }
    };

    const handleVerifyResetCode = async () => {
        if (!resetCode || resetCode.length !== 6) {
            setResetError('Введите 6-значный код подтверждения');
            return;
        }

        setResetLoading(true);
        setResetError(null);

        try {
            const response = await fetch(`${API_URL}/api/auth/verify-password-reset-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: resetEmail, code: resetCode })
            });

            const data = await response.json();

            if (data.success) {
                setResetStep('password');
                setResetSuccess(null);
            } else {
                setResetError(data.error || 'Неверный или просроченный код');
            }
        } catch (error) {
            console.error('Ошибка:', error);
            setResetError('Ошибка соединения с сервером');
        } finally {
            setResetLoading(false);
        }
    };

    const handleResetPassword = async () => {
        if (newPassword.length < 6) {
            setResetError('Пароль должен быть не менее 6 символов');
            return;
        }

        if (newPassword !== confirmNewPassword) {
            setResetError('Пароли не совпадают');
            return;
        }

        setResetLoading(true);
        setResetError(null);

        try {
            const response = await fetch(`${API_URL}/api/auth/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: resetEmail,
                    code: resetCode,
                    newPassword: newPassword
                })
            });

            const data = await response.json();

            if (data.success) {
                setResetSuccess('Пароль успешно изменен! Теперь вы можете войти.');
                setTimeout(() => {
                    onClose();
                    if (onSuccess) onSuccess();
                }, 2000);
            } else {
                setResetError(data.error || 'Ошибка при смене пароля');
            }
        } catch (error) {
            console.error('Ошибка:', error);
            setResetError('Ошибка соединения с сервером');
        } finally {
            setResetLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Восстановление пароля</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>
                <div className="modal-body">
                    {resetSuccess && (
                        <div className="reset-success">{resetSuccess}</div>
                    )}

                    {resetError && (
                        <div className="reset-error">{resetError}</div>
                    )}

                    {resetStep === 'email' && (
                        <>
                            <div className="verify-icon">🔐</div>
                            <p>Введите email, привязанный к аккаунту:</p>
                            <div className="code-input-group">
                                <input
                                    type="email"
                                    className="code-input-full"
                                    placeholder="Email"
                                    value={resetEmail}
                                    onChange={(e) => setResetEmail(e.target.value)}
                                    autoFocus
                                />
                            </div>
                            <div className="verify-actions">
                                <button
                                    className="confirm-verify-btn"
                                    onClick={handleRequestResetCode}
                                    disabled={resetLoading}
                                >
                                    {resetLoading ? 'Отправка...' : 'Отправить код'}
                                </button>
                                <button
                                    className="close-verify-btn"
                                    onClick={onClose}
                                >
                                    Отмена
                                </button>
                            </div>
                        </>
                    )}

                    {resetStep === 'code' && (
                        <>
                            <div className="verify-icon">📧</div>
                            <p>Код отправлен на email:</p>
                            <p className="verify-email">{resetEmail}</p>
                            <div className="code-input-group">
                                <input
                                    type="text"
                                    maxLength="6"
                                    className="code-input-full"
                                    placeholder="Введите 6-значный код"
                                    value={resetCode}
                                    onChange={(e) => setResetCode(e.target.value.replace(/\D/g, ''))}
                                    autoFocus
                                />
                            </div>
                            <p className="verify-text-small">
                                Код действителен 15 минут
                            </p>
                            <div className="verify-actions">
                                <button
                                    className="confirm-verify-btn"
                                    onClick={handleVerifyResetCode}
                                    disabled={resetLoading}
                                >
                                    {resetLoading ? 'Проверка...' : 'Подтвердить код'}
                                </button>
                                <button
                                    className="resend-verify-btn"
                                    onClick={handleRequestResetCode}
                                    disabled={resetLoading}
                                >
                                    Отправить повторно
                                </button>
                            </div>
                        </>
                    )}

                    {resetStep === 'password' && (
                        <>
                            <div className="verify-icon">🔑</div>
                            <p>Придумайте новый пароль:</p>
                            <div className="code-input-group">
                                <input
                                    type="password"
                                    className="code-input-full"
                                    placeholder="Новый пароль (минимум 6 символов)"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    autoFocus
                                />
                                <input
                                    type="password"
                                    className="code-input-full"
                                    style={{ marginTop: '10px' }}
                                    placeholder="Подтвердите новый пароль"
                                    value={confirmNewPassword}
                                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                                />
                            </div>
                            <div className="verify-actions">
                                <button
                                    className="confirm-verify-btn"
                                    onClick={handleResetPassword}
                                    disabled={resetLoading}
                                >
                                    {resetLoading ? 'Сохранение...' : 'Сохранить пароль'}
                                </button>
                                <button
                                    className="close-verify-btn"
                                    onClick={onClose}
                                >
                                    Отмена
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export function AuthPage({ onAuthSuccess }) {
    const [isLogin, setIsLogin] = useState(true);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [showResend, setShowResend] = useState(false);
    const [resendEmail, setResendEmail] = useState('');
    const [resendLoading, setResendLoading] = useState(false);
    const [resendMessage, setResendMessage] = useState(null);
    const [showTelegramAuth, setShowTelegramAuth] = useState(false);
    const [showVerifyModal, setShowVerifyModal] = useState(false);
    const [unverifiedEmail, setUnverifiedEmail] = useState('');
    const [showResetPassword, setShowResetPassword] = useState(false);

    // Проверяем токен в URL
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');
        const userParam = urlParams.get('user');

        console.log('🔍 AuthPage - checking URL for token');

        if (token && userParam) {
            console.log('✅ Token found in URL');
            try {
                const user = JSON.parse(decodeURIComponent(userParam));
                console.log('📦 User data from URL (полные данные):', user);
                console.log('📦 user.id:', user.id);
                console.log('📦 user.telegram_id:', user.telegram_id);

                if (!user.id && user.telegram_id) {
                    user.id = user.telegram_id;
                    console.log('⚠️ id отсутствовал, установлен из telegram_id:', user.id);
                }

                if (!user.id) {
                    console.error('❌ Нет id в данных пользователя!');
                    setError('Ошибка: не удалось получить ID пользователя');
                    return;
                }

                localStorage.setItem('token', token);
                localStorage.setItem('user', JSON.stringify(user));

                console.log('✅ User saved, calling onAuthSuccess with id:', user.id);
                onAuthSuccess(user);
            } catch (e) {
                console.error('Error parsing user:', e);
                setError('Ошибка обработки данных авторизации');
            }
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, [onAuthSuccess]);

    // Загружаем Telegram виджет
    useEffect(() => {
        const oldScript = document.getElementById('telegram-widget-script');
        if (oldScript) oldScript.remove();

        const script = document.createElement('script');
        script.id = 'telegram-widget-script';
        script.src = 'https://telegram.org/js/telegram-widget.js?22';
        script.async = true;
        script.setAttribute('data-telegram-login', 'PlggBot');
        script.setAttribute('data-size', 'large');
        script.setAttribute('data-radius', '16');
        script.setAttribute('data-auth-url', `${API_URL}/api/auth/telegram-callback`);
        script.setAttribute('data-request-access', 'write');

        const container = document.getElementById('telegram-widget-container');
        if (container) {
            container.innerHTML = '';
            container.appendChild(script);
        }
    }, []);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        setError(null);
        setSuccessMessage(null);
        setShowResend(false);
    };

    const handleResendVerification = async () => {
        setResendLoading(true);
        setResendMessage(null);

        try {
            const response = await fetch(`${API_URL}/api/auth/resend-verification`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: resendEmail })
            });

            const data = await response.json();

            if (data.success) {
                setResendMessage({ type: 'success', text: data.message });
                setTimeout(() => setResendMessage(null), 5000);
            } else {
                setResendMessage({ type: 'error', text: data.error });
            }
        } catch (error) {
            setResendMessage({ type: 'error', text: 'Ошибка соединения с сервером' });
        } finally {
            setResendLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccessMessage(null);
        setShowResend(false);

        try {
            if (isLogin) {
                const response = await fetch(`${API_URL}/api/auth/login`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        email: formData.email,
                        password: formData.password
                    })
                });

                const data = await response.json();

                if (response.ok && data.success) {
                    if (!data.user.emailVerified) {
                        setUnverifiedEmail(formData.email);
                        setShowVerifyModal(true);
                        setLoading(false);
                        return;
                    }

                    localStorage.setItem('token', data.token);
                    localStorage.setItem('user', JSON.stringify(data.user));
                    onAuthSuccess(data.user);
                } else {
                    if (data.error === 'Email not verified') {
                        setError('Пожалуйста, подтвердите ваш email. Проверьте почту.');
                        setShowResend(true);
                        setResendEmail(formData.email);
                    } else {
                        setError(data.error || 'Ошибка входа');
                    }
                }
            } else {
                if (formData.password !== formData.confirmPassword) {
                    setError('Пароли не совпадают');
                    setLoading(false);
                    return;
                }

                if (formData.password.length < 6) {
                    setError('Пароль должен быть не менее 6 символов');
                    setLoading(false);
                    return;
                }

                const response = await fetch(`${API_URL}/api/auth/register`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        email: formData.email,
                        password: formData.password
                    })
                });

                const data = await response.json();

                if (response.ok && data.success) {
                    setSuccessMessage(data.message);
                    setFormData({
                        email: '',
                        password: '',
                        confirmPassword: ''
                    });
                    setTimeout(() => {
                        setIsLogin(true);
                        setSuccessMessage(null);
                    }, 3000);
                } else {
                    setError(data.error || 'Ошибка регистрации');
                }
            }
        } catch (error) {
            console.error('Ошибка:', error);
            setError('Ошибка соединения с сервером');
        } finally {
            setLoading(false);
        }
    };

    // Модальное окно для неподтвержденного email
    const VerifyModal = () => (
        <div className="modal-overlay" onClick={() => setShowVerifyModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Подтверждение email</h2>
                    <button className="modal-close" onClick={() => setShowVerifyModal(false)}>×</button>
                </div>
                <div className="modal-body">
                    <div className="verify-icon">📧</div>
                    <p>Пожалуйста, подтвердите ваш email адрес:</p>
                    <p className="verify-email">{unverifiedEmail}</p>
                    <p className="verify-text">
                        На указанный email было отправлено письмо с ссылкой для подтверждения.
                        Перейдите по ссылке, чтобы активировать аккаунт.
                    </p>
                    <div className="verify-actions">
                        <button
                            className="resend-verify-btn"
                            onClick={async () => {
                                await handleResendVerification();
                                setShowVerifyModal(false);
                            }}
                            disabled={resendLoading}
                        >
                            {resendLoading ? 'Отправка...' : 'Отправить письмо повторно'}
                        </button>
                        <button
                            className="close-verify-btn"
                            onClick={() => setShowVerifyModal(false)}
                        >
                            Закрыть
                        </button>
                    </div>
                    {resendMessage && (
                        <div className={`verify-message ${resendMessage.type}`}>
                            {resendMessage.text}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    if (showTelegramAuth) {
        return (
            <TelegramAuth
                onAuthSuccess={onAuthSuccess}
                onBack={() => setShowTelegramAuth(false)}
            />
        );
    }

    return (
        <div className="auth-page">
            <div className="auth-container">
                <div className="auth-card">
                    <div className="auth-header">
                        <h1>HydraVPN</h1>
                        <p>{isLogin ? 'Войдите в свой аккаунт' : 'Создайте новый аккаунт'}</p>
                    </div>

                    {error && (
                        <div className="auth-error">
                            {error}
                        </div>
                    )}

                    {successMessage && (
                        <div className="auth-success">
                            ✅ {successMessage}
                        </div>
                    )}

                    {resendMessage && resendMessage.type !== 'success' && (
                        <div className={`auth-message ${resendMessage.type}`}>
                            {resendMessage.text}
                        </div>
                    )}

                    {showResend && (
                        <div className="resend-container">
                            <p className="resend-text">
                                Не пришло письмо?
                                <button
                                    className="resend-button"
                                    onClick={handleResendVerification}
                                    disabled={resendLoading}
                                >
                                    {resendLoading ? 'Отправка...' : 'Отправить повторно'}
                                </button>
                            </p>
                        </div>
                    )}

                    {/* Виджет входа через Telegram */}
                    {isLogin && (
                        <>
                            <button
                                onClick={() => setShowTelegramAuth(true)}
                                className="telegram-auth-button"
                            >
                                Войти через Telegram
                            </button>
                            <div className="auth-divider">
                                <span>или</span>
                            </div>
                        </>
                    )}

                    <form onSubmit={handleSubmit} className="auth-form">
                        <div className="form-group">
                            <input
                                type="email"
                                name="email"
                                placeholder="Email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <input
                                type="password"
                                name="password"
                                placeholder="Пароль"
                                value={formData.password}
                                onChange={handleChange}
                                required
                                minLength={6}
                            />
                        </div>

                        {/* Ссылка "Забыл пароль" */}
                        {isLogin && (
                            <div className="forgot-password-container">
                                <button
                                    type="button"
                                    className="forgot-password-link"
                                    onClick={() => setShowResetPassword(true)}
                                >
                                    Забыли пароль?
                                </button>
                            </div>
                        )}

                        {!isLogin && (
                            <div className="form-group">
                                <input
                                    type="password"
                                    name="confirmPassword"
                                    placeholder="Подтвердите пароль"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    required
                                    minLength={6}
                                />
                            </div>
                        )}

                        <button type="submit" className="auth-button" disabled={loading}>
                            {loading ? 'Загрузка...' : (isLogin ? 'Войти' : 'Зарегистрироваться')}
                        </button>
                    </form>

                    <div className="auth-footer">
                        <button
                            className="auth-switch"
                            onClick={() => {
                                setIsLogin(!isLogin);
                                setError(null);
                                setSuccessMessage(null);
                                setShowResend(false);
                                setResendMessage(null);
                                setShowVerifyModal(false);
                                setFormData({
                                    email: '',
                                    password: '',
                                    confirmPassword: ''
                                });
                            }}
                        >
                            {isLogin ? 'Нет аккаунта? Зарегистрироваться' : 'Уже есть аккаунт? Войти'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Модальное окно для неподтвержденного email */}
            {showVerifyModal && <VerifyModal />}

            {/* Модальное окно для восстановления пароля - вынесено в отдельный компонент */}
            <ResetPasswordModal
                isOpen={showResetPassword}
                onClose={() => setShowResetPassword(false)}
                onSuccess={() => {}}
            />
        </div>
    );
}